import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Text } from "@ttc3k/trekker";
import _ from "lodash";
import {
  QuestionnaireResponse,
  useBookerContext,
  useCartContext,
} from "widgets/booker/contexts";
import {
  ActionCard,
  CollapsableStep,
  QuestionnaireQuestionList,
} from "widgets/booker/shared";
import { QuestionnairePage } from "widgets/booker/features/OverlayPages";
import { useTranslation } from "react-i18next";
import { getMultilingualString } from "shared/utils";
import {
  getOverriddenQuestionnaireDescription,
  getOverriddenQuestionnaireTitle,
} from "widgets/booker/shared/utils";
import {
  areRequiredQuestionnaireResponsesAnswered,
  canShowQuestionnaireInline,
  clearHiddenResponses,
  isSurveyDesignFieldType,
  replaceOwnedQuestionnaireResponses,
} from "shared/components";
import { getDefaultSurveyQuestionResponse } from "shared/components/organisms/SurveyQuestionInput/surveyQuestionCheckbox";

export const Questionnaire = () => {
  const { questionnaireComplete } = useCartContext();
  const { booker, language } = useBookerContext();

  const { t } = useTranslation("booker", { keyPrefix: "QUESTIONNAIRE" });

  const [isQuestionnairePageOpen, setIsQuestionnairePageOpen] = useState(false);

  const isSurveyRequired = booker?.survey?.questions.some(
    q => q.required && q.isActive,
  );

  if (!booker?.survey) return null;

  const isInline =
    !!booker.surveyInline && canShowQuestionnaireInline(booker.survey);

  return (
    <CollapsableStep
      title={getOverriddenQuestionnaireTitle(booker, language) || t("TITLE")}
      complete={questionnaireComplete}
      stepId="questionnaire"
    >
      <Text color="text.mid" whiteSpace="pre-line">
        {getOverriddenQuestionnaireDescription(booker, t("SUBTITLE"), language)}
      </Text>
      {isInline ? (
        <InlineQuestionnaire hasRequiredQuestions={!!isSurveyRequired} />
      ) : (
        <>
          <ActionCard
            mainText={getMultilingualString(booker.survey.name, language)}
            subText={isSurveyRequired ? t("REQUIRED") : t("OPTIONAL")}
            visual={questionnaireComplete ? "success" : "neutral"}
            onClick={() => setIsQuestionnairePageOpen(true)}
          />
          <QuestionnairePage
            open={isQuestionnairePageOpen}
            onClose={() => setIsQuestionnairePageOpen(false)}
          />
        </>
      )}
    </CollapsableStep>
  );
};

const InlineQuestionnaire = ({
  hasRequiredQuestions,
}: {
  hasRequiredQuestions: boolean;
}) => {
  const { booker } = useBookerContext();
  const {
    questionnaireResponses,
    updateQuestionnaireResponses,
    questionnaireComplete,
    setQuestionnaireCompleteState,
  } = useCartContext();

  const questions = useMemo(
    () => (booker?.survey?.questions ?? []).filter(q => q.isActive),
    [booker?.survey?.questions],
  );

  const ownedQuestionIds = useMemo(
    () =>
      new Set(
        (booker?.survey?.questions ?? [])
          .map(q => q._id)
          .filter((id): id is string => !!id),
      ),
    [booker?.survey?.questions],
  );

  const seedResponses = useCallback(
    (cartResponses: QuestionnaireResponse[]) =>
      questions.map(q => ({
        questionData: q,
        response:
          cartResponses.find(r => r.questionData._id === q._id)?.response ??
          getDefaultSurveyQuestionResponse(q),
      })),
    [questions],
  );

  const [responses, setResponses] = useState<QuestionnaireResponse[]>(() =>
    seedResponses(questionnaireResponses),
  );
  const [touched, setTouched] = useState(false);
  const lastOwnedSignature = useRef<string | null>(null);

  // Re-seed when checkout answers change underneath us (snapshot hydration,
  // reset). Pricing-survey writes share this cart list and must not reset
  // the form or the "touched" flag.
  useEffect(() => {
    const nextSignature = ownedResponseSignature(
      questionnaireResponses,
      ownedQuestionIds,
    );
    if (lastOwnedSignature.current === null) {
      lastOwnedSignature.current = nextSignature;
      return;
    }
    if (nextSignature === lastOwnedSignature.current) return;
    lastOwnedSignature.current = nextSignature;
    setResponses(seedResponses(questionnaireResponses));
    setTouched(false);
  }, [questionnaireResponses, ownedQuestionIds, seedResponses]);

  const isRequiredAnswered = useMemo(
    () => areRequiredQuestionnaireResponsesAnswered(responses),
    [responses],
  );

  // Optional-only questionnaires stay unchecked until the customer answers
  // something, matching the overlay where the checkmark follows "Done".
  useEffect(() => {
    if (!hasRequiredQuestions && !touched) return;
    if (isRequiredAnswered !== questionnaireComplete) {
      setQuestionnaireCompleteState(isRequiredAnswered);
    }
  }, [
    hasRequiredQuestions,
    touched,
    isRequiredAnswered,
    questionnaireComplete,
    setQuestionnaireCompleteState,
  ]);

  const handleResponseChange = (questionId: string, response: string[]) => {
    const next = clearHiddenResponses(
      responses.map(r =>
        r.questionData._id === questionId ? { ...r, response } : r,
      ),
    );
    const cartResponses = next
      .filter(r => !isSurveyDesignFieldType(r.questionData.type))
      .map(r => ({ ...r, response: _.compact(r.response) }));
    setResponses(next);
    setTouched(true);
    updateQuestionnaireResponses(current => {
      const merged = replaceOwnedQuestionnaireResponses(
        current,
        ownedQuestionIds,
        cartResponses,
      );
      lastOwnedSignature.current = ownedResponseSignature(
        merged,
        ownedQuestionIds,
      );
      return merged;
    });
  };

  return (
    <QuestionnaireQuestionList
      questions={responses.map(r => r.questionData)}
      getResponse={q =>
        responses.find(r => r.questionData._id === q._id)?.response ?? []
      }
      onResponseChange={handleResponseChange}
    />
  );
};

const ownedResponseSignature = (
  responses: QuestionnaireResponse[],
  ownedIds: Set<string>,
) =>
  responses
    .filter(response => ownedIds.has(response.questionData._id ?? ""))
    .map(
      response =>
        `${response.questionData._id}:${(response.response ?? []).join("\u0001")}`,
    )
    .sort()
    .join("\u0002");
